export const AUDIENCE_PRESETS = [
  {
    key: "all_eligible",
    label: "All eligible contacts",
    shortLabel: "All Eligible",
    description: "Reach every WhatsApp contact that passes delivery guardrails.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 7,
    manualSelection: false,
  },
  {
    key: "never_contacted",
    label: "Never contacted",
    shortLabel: "Never Contacted",
    description: "Only contacts who have never received a WhatsApp broadcast.",
    segment: "never_contacted",
    defaultExcludeRecent: false,
    defaultDays: 7,
    manualSelection: false,
  },
  {
    key: "hot_leads",
    label: "Hot leads",
    shortLabel: "Hot Leads",
    description: "Focus only on hot or urgent leads that have a reachable phone number.",
    segment: "hot_leads",
    defaultExcludeRecent: false,
    defaultDays: 7,
    manualSelection: false,
  },
  {
    key: "reengage_30",
    label: "Re-engage 30+ days",
    shortLabel: "Re-engage 30+",
    description: "Bring back contacts who have not been messaged on WhatsApp in the last 30 days.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 30,
    manualSelection: false,
  },
  {
    key: "custom_audience",
    label: "Custom audience",
    shortLabel: "Custom Audience",
    description: "Use filters, review contacts manually, or import a phone list from the picker in the next step.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 7,
    manualSelection: true,
  },
  {
    key: "my_contacts",
    label: "My contacts",
    shortLabel: "My Contacts",
    description: "Message contacts you created in My Contacts that have a valid phone.",
    segment: "",
    defaultExcludeRecent: false,
    defaultDays: 7,
    manualSelection: false,
  },
  {
    key: "ai_intelligence",
    label: "AI Intelligence segment",
    shortLabel: "AI Segment",
    description: "Use an audience segment approved in the Intelligence Hub weekly plan.",
    segment: "",
    defaultExcludeRecent: true,
    defaultDays: 7,
    manualSelection: false,
  },
];

export function getAudiencePreset(key) {
  return AUDIENCE_PRESETS.find((preset) => preset.key === key) ?? AUDIENCE_PRESETS[0];
}

export function isManualAudience(audienceKey) {
  return audienceKey === "custom_audience";
}

export function normalizePositiveInteger(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  return Math.max(1, Math.floor(numeric));
}

export function toAudienceRow(contact) {
  return {
    id: String(contact.id ?? contact._tmp ?? contact.primary_phone ?? contact.phone_number ?? `row-${Math.random().toString(16).slice(2)}`),
    name: contact.display_name || contact.name || contact.primary_phone || contact.phone_number || "",
    phone: String(contact.primary_phone || contact.phone_number || ""),
    email: String(contact.primary_email || contact.email || "").toLowerCase(),
    last_contacted_at: contact.last_contacted_at || null,
    lead_status: contact.lead_status || null,
    is_hot_client: Boolean(contact.is_hot_client),
    _source: contact.source || contact.primary_source || "manual",
  };
}

export function buildAudiencePlan({
  audienceKey,
  filters,
  excludeRecent,
  recentDays,
}) {
  const preset = getAudiencePreset(audienceKey);
  const preferredLimit = normalizePositiveInteger(filters.preferred_limit);

  const baseFilters = {
    q: filters.q || "",
    source: filters.source || null,
    status: filters.status || null,
    contact_group_id: filters.contact_group_id || null,
    segment: preset.segment || filters.segment || "",
    exclude_recent: excludeRecent,
    days: Number(recentDays || preset.defaultDays || 7),
  };

  if (preferredLimit) {
    baseFilters.preferred_limit = preferredLimit;
  }

  if (audienceKey === "my_contacts") {
    baseFilters.audience_kind = "my_contacts";
  }
  if (audienceKey === "all_eligible") {
    baseFilters.include_my_contacts = true;
  }

  if (preset.manualSelection) {
    return {
      audienceKey,
      deliveryMode: "ids",
      manualSelection: true,
      label: preset.label,
      filters: {
        ...baseFilters,
        limit: Number(filters.limit || 200),
      },
    };
  }

  return {
    audienceKey,
    deliveryMode: "filter_all",
    manualSelection: false,
    label: preset.label,
    filters: baseFilters,
  };
}

export function buildAudienceNarrative({
  audienceKey,
  filters,
  summary,
}) {
  const preset = getAudiencePreset(audienceKey);
  const sendable = Number(summary?.eligibleFound || 0);
  const matched = Number(summary?.totalFound || sendable || 0);
  const suppressed = Number(summary?.suppressedFound || 0);
  const preferredLimit = normalizePositiveInteger(summary?.preferredLimit || filters?.preferred_limit);
  const preferredSuppressed = Number(summary?.suppressedBreakdown?.["Preferred campaign limit reached"] || 0);
  const sourceLabel = filters?.source ? sourceLabelFor(filters.source) : "all connected sources";

  if (preset.manualSelection) {
    const readySentence = matched > 0
      ? `${formatCount(matched)} matched contacts are ready for manual review.`
      : "Run Find Audience to review matching contacts.";
    return `You are building a custom WhatsApp audience from ${sourceLabel}. ${readySentence}${preferredSentence(preferredLimit, preferredSuppressed)}`;
  }

  const sendSentence = `This broadcast will send to ${formatCount(sendable)} ${preset.label.toLowerCase()} from ${sourceLabel}.`;

  if (suppressed > 0) {
    return `${sendSentence} ${formatCount(suppressed)} will be skipped because of recent-contact, duplicate, or budget guardrails.${preferredSentence(preferredLimit, preferredSuppressed)}`;
  }

  return `${sendSentence}${preferredSentence(preferredLimit, preferredSuppressed)}`;
}

export function buildAudienceStatusBadges(summary) {
  return {
    matched: Number(summary?.totalFound || 0),
    sendable: Number(summary?.eligibleFound || 0),
    skipped: Number(summary?.suppressedFound || 0),
  };
}

function sourceLabelFor(source) {
  return {
    hubspot: "HubSpot",
    google: "Google Contacts",
    inquiries: "Inquiries",
    excel: "Excel imports",
    manual: "manual contacts",
  }[source] || source;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function preferredSentence(preferredLimit, preferredSuppressed) {
  if (preferredSuppressed > 0 && preferredLimit) {
    return ` ${formatCount(preferredSuppressed)} more are being held back by your preferred send limit of ${formatCount(preferredLimit)}.`;
  }

  if (preferredLimit) {
    return ` Preferred send limit: ${formatCount(preferredLimit)}.`;
  }

  return "";
}

// Mirrors CampaignAudienceResolver::TARGET_AUDIENCES (contacts.category rule).
export const TARGET_AUDIENCE_OPTIONS = [
  { value: "all", label: "All contacts" },
  { value: "exclude_vip", label: "Everyone except VIPs" },
  { value: "standard", label: "Standard contacts only" },
  { value: "vip", label: "VIPs only" },
];

export function targetAudienceLabel(value) {
  return (TARGET_AUDIENCE_OPTIONS.find((option) => option.value === value) || TARGET_AUDIENCE_OPTIONS[0]).label;
}
